import React from 'react';
import { StationSector } from '../../../../../../utils/types';
import { FormInput, FormSection, FormAutocomplete, TimeInputWithPicker } from '../../../../common';
import { useStationFormatters, useLocalTimeHelper } from '../hooks';
import { useTimezone } from '../../../../../../contexts/TimezoneContext';
import { utcToMYT, mytToUTC } from '../../../../../../utils/timeUtils';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import FlightLandIcon from '@mui/icons-material/FlightLand';
import type { AirportAutocomplete } from '../../../../../../services/redux/api/airport.api';

interface StationFormSectionProps {
  station: StationSector;
  sectorNumber: number;
  isDeparture: boolean;
  sectorIndex: number;
  onUpdate: (key: keyof StationSector, value: StationSector[keyof StationSector]) => void;
  getFieldError?: (sectorIndex: number, fieldName: keyof StationSector) => string | null;
  handleBlur?: (sectorIndex: number, fieldName: keyof StationSector, value: StationSector[keyof StationSector], station?: StationSector) => void;
  revalidateIfTouched?: (sectorIndex: number, fieldName: keyof StationSector, value: StationSector[keyof StationSector], station?: StationSector) => void;
}

export function StationFormSection({
  station,
  sectorNumber,
  isDeparture,
  sectorIndex,
  onUpdate,
  getFieldError,
  handleBlur,
  revalidateIfTouched,
}: StationFormSectionProps) {
  const { formatIataCode, formatUtcOffset, padUtcOffset, formatTimeInput } = useStationFormatters();
  const { getLocalTimeHelper } = useLocalTimeHelper();
  const { timezone } = useTimezone();
  const isMYT = timezone === 'MYT';

  const stationCode = isDeparture ? station.departureStation : station.arrivalStation;
  const terminal = isDeparture ? station.passengerTerminalDep : station.passengerTerminalArr;
  const time = isDeparture ? station.std : station.sta;
  const utcVariation = isDeparture ? station.utcLocalTimeVariationDep : station.utcLocalTimeVariationArr;
  const dayChange = isDeparture ? (station.cd ?? 0) : (station.ca ?? 0);

  const timeLabel = isDeparture ? 'STD' : 'STA';

  // Calculate station number: Sector 1 = Station 1-2, Sector 2 = Station 3-4, etc.
  const stationNumber = sectorIndex * 2 + (isDeparture ? 1 : 2);

  const stationCodeField = isDeparture ? 'departureStation' : 'arrivalStation';
  const terminalField = isDeparture ? 'passengerTerminalDep' : 'passengerTerminalArr';
  const timeField = isDeparture ? 'std' : 'sta';
  const utcVariationField = isDeparture ? 'utcLocalTimeVariationDep' : 'utcLocalTimeVariationArr';
  const dayChangeField = isDeparture ? 'cd' : 'ca';

  const handleStationCodeChange = (value: string) => {
    const formatted = formatIataCode(value);
    onUpdate(stationCodeField, formatted);
    revalidateIfTouched?.(sectorIndex, stationCodeField, formatted, station);
  };

  const handleAirportSelect = (airport: AirportAutocomplete) => {
    // Auto-populate UTC variation when airport is selected
    const formattedCode = formatIataCode(airport.iataAirportCode || '');
    const formattedUtc = padUtcOffset(airport.utcVariant || '');
    onUpdate(stationCodeField, formattedCode);
    onUpdate(utcVariationField, formattedUtc);

    // Revalidate both fields
    revalidateIfTouched?.(sectorIndex, stationCodeField, formattedCode, station);
    revalidateIfTouched?.(sectorIndex, utcVariationField, formattedUtc);
  };

  const handleTerminalChange = (value: string) => {
    onUpdate(terminalField, value);
    revalidateIfTouched?.(sectorIndex, terminalField, value);
  };

  // Display value: convert stored UTC to MYT when in MYT mode
  const displayTimeValue = isMYT && time && time.length === 4 && /^\d{4}$/.test(time)
    ? utcToMYT(time)
    : time;

  const handleTimeChange = (value: string) => {
    const formatted = formatTimeInput(value);
    // Convert MYT input to UTC for storage when in MYT mode
    const toStore = isMYT && formatted.length === 4 && /^\d{4}$/.test(formatted)
      ? mytToUTC(formatted)
      : formatted;
    onUpdate(timeField, toStore);
    revalidateIfTouched?.(sectorIndex, timeField, toStore);
  };

  const handleUtcVariationChange = (value: string) => {
    const formatted = formatUtcOffset(value);
    onUpdate(utcVariationField, formatted);
    revalidateIfTouched?.(sectorIndex, utcVariationField, formatted);
  };

  const handleUtcVariationBlur = () => {
    const padded = padUtcOffset(utcVariation || '');
    if (padded !== utcVariation) {
      onUpdate(utcVariationField, padded);
    }
    handleBlur?.(sectorIndex, utcVariationField, padded);
  };

  const handleDayChangeChange = (value: string) => {
    const parsed = parseInt(value) || 0;
    onUpdate(dayChangeField, parsed);
    revalidateIfTouched?.(sectorIndex, dayChangeField, parsed);
  };

  return (
    <FormSection
      title={`Sector ${sectorNumber} - Station ${stationNumber}`}
      icon={isDeparture ? <FlightTakeoffIcon sx={{ fontSize: 20 }} /> : <FlightLandIcon sx={{ fontSize: 20 }} />}
      collapsible={false}
    >
      <div className="grid grid-cols-5 gap-x-4">
        <FormAutocomplete
          label={`Station ${stationNumber} Code`}
          value={stationCode}
          onChange={handleStationCodeChange}
          onSelect={handleAirportSelect}
          onBlur={(value) => handleBlur?.(sectorIndex, stationCodeField, value, station)}
          required
          error={getFieldError?.(sectorIndex, stationCodeField)}
        />
        <FormInput
          label={`Terminal (S${stationNumber})`}
          value={terminal || ''}
          onChange={handleTerminalChange}
          onBlur={() => handleBlur?.(sectorIndex, terminalField, terminal)}
          error={getFieldError?.(sectorIndex, terminalField)}
          readOnly={true}
        />
        <div>
          <TimeInputWithPicker
            label={`${timeLabel} (${timezone})`}
            value={displayTimeValue}
            onChange={handleTimeChange}
            onBlur={() => handleBlur?.(sectorIndex, timeField, time)}
            required
            error={getFieldError?.(sectorIndex, timeField)}
          />
          {!isMYT && getLocalTimeHelper(time, utcVariation || '', dayChange || 0)}
        </div>
        {isMYT ? (
          <FormInput
            label={`${timeLabel} (UTC)`}
            value={time && time.length === 4 && /^\d{4}$/.test(time) ? `${time.slice(0,2)}:${time.slice(2)}` : time || ''}
            onChange={() => {}}
            readOnly={true}
          />
        ) : (
          <FormInput
            label={`UTC Variation (S${stationNumber})`}
            value={utcVariation || ''}
            onChange={handleUtcVariationChange}
            onBlur={handleUtcVariationBlur}
            required
            readOnly={true}
            error={getFieldError?.(sectorIndex, utcVariationField)}
          />
        )}
        <FormInput
          label={`Day Change (S${stationNumber})`}
          value={dayChange || 0}
          onChange={handleDayChangeChange}
          onBlur={() => handleBlur?.(sectorIndex, dayChangeField, dayChange)}
          type="number"
          readOnly={true}
          error={getFieldError?.(sectorIndex, dayChangeField)}
        />
      </div>
    </FormSection>
  );
}
